import type {AIChatNegativeFeedbackTypes} from '@Components/ai-chat-window/components/feedback-popup/feedback-popup.types';
import type {ChatMessageComponentPart} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import type {SurveySpec} from '@Components/ai-chat-window/components/survey/survey.types';
import type {ChatDocumentType} from '@Libraries/chat-document.types';
import type {AiAgentMode} from '@/ai-chat/providers/ai-chat-provider.types';

export const FLOATING_PROMPT_BOX_SELECTOR = '[data-floating-prompt-box]';

export interface Usage {
  inputTokens: number;
  outputTokens: number;
}

export enum MessageRole {
  USER = 'user',
  ASSISTANT = 'assistant',
  DEVELOPER = 'developer',
}

export enum ChatMessagePartType {
  TEXT = 'text',
  USER_IMAGE = 'user_image',
  USER_DOCUMENT = 'user_document',
  EXAMPLE_IMAGE_ASSET = 'example_image_asset',
  DESIGN = 'design',
  MODEL_GENERATED_IMAGE = 'image',
  IMAGE_GRAPHIC_ITEM_SNAPSHOT = 'image_graphic_item_snapshot',
  REATTACHED_MODEL_IMAGE = 'reattached_model_image',
  COMPONENT = 'component',
}

export enum TextPartPurpose {
  CHANGE_STYLE = 'change_style',
}

/** The entity card the user clicked last, named to the server with their next message so "this one" resolves; the server finds the card in the chat and writes what the agent reads. */
export interface PointedAtEntity {
  messageId: string;
  entityType: string;
  entityId: string;
}

/** The options the user picked in a form, by the form and step they belong to; the server checks them against what it showed. */
export interface SurveyPicks {
  surveyId: string;
  picks: Record<string, string[]>;
}

export interface ChatMessageTextPart {
  type: ChatMessagePartType.TEXT;
  text: string;
  loadingTexts?: string[];
  purpose?: TextPartPurpose;
  isNextSuggestion?: boolean;
}

export interface ChatMessageUserImagePart {
  type: ChatMessagePartType.USER_IMAGE;
  hashedFilename: string;
  extension: string;
  source: string;
}

export interface ChatMessageUserDocumentPart {
  type: ChatMessagePartType.USER_DOCUMENT;
  documentHashedId: string;
  filename: string;
  documentType: ChatDocumentType;
}

export interface ChatMessageExampleImageAssetPart {
  type: ChatMessagePartType.EXAMPLE_IMAGE_ASSET;
  fileName: string;
}

export interface ChatMessageImageGraphicItemSnapshotPart {
  type: ChatMessagePartType.IMAGE_GRAPHIC_ITEM_SNAPSHOT;
  url: string;
}

export interface ChatMessageDesignPart {
  type: ChatMessagePartType.DESIGN;
  hashedId: string;
  previewUrl?: string;
  pageHashedId?: string;
}

export interface ReattachedModelImage {
  type: ChatMessagePartType.REATTACHED_MODEL_IMAGE;
  hashedId: string;
}

export interface SelectedImageAssetPart {
  type: string;
  hashedFilename?: string;
  extension?: string;
  hashedId?: string;
  pageHashedId?: string | null;
  fileName?: string;
  url?: string;
  image?: SelectedImageAssetPart;
}

/**
 * AssetSelectionAgent's per-asset role classification (debug surface only).
 * Mirrors the PHP enum App\Libraries\Facades\Neuron\Workflows\AiChatImageEditor\Responses\AssetRole.
 */
export enum SelectedImageAssetRole {
  TARGET = 'target',
  CONTENT_SLOT = 'content_slot',
  BRAND_MARK = 'brand_mark',
  STYLE_REFERENCE = 'style_reference',
  IDENTITY_ANCHOR = 'identity_anchor',
  POSITIONAL_ANCHOR = 'positional_anchor',
}

/**
 * ImageDescriptionAgent's structural classification for an asset (debug surface only).
 * Mirrors the PHP enum App\Libraries\Facades\Neuron\Workflows\AiChatImageEditor\Responses\AssetShape.
 */
export enum SelectedImageAssetShape {
  PHOTOGRAPHIC_SUBJECT = 'photographic_subject',
  SINGLE_MARK = 'single_mark',
  COMPOSED_DESIGN = 'composed_design',
}

/**
 * Framing classification on composed_design assets (debug surface only).
 * Mirrors the PHP enum App\Libraries\Facades\Neuron\Workflows\AiChatImageEditor\Responses\AssetFraming.
 */
export enum SelectedImageAssetFraming {
  FULL_BLEED = 'full_bleed',
  MOCKUP_FRAMED = 'mockup_framed',
}

/**
 * AssetSelectionAgent's top-level routing decision for the turn (debug surface only).
 * Mirrors the PHP enum App\Libraries\Facades\Neuron\Workflows\AiChatImageEditor\Responses\AssetMode.
 */
export enum ImageGenerationContextMode {
  CREATE = 'create',
  EDIT = 'edit',
  TEMPLATE_FILL = 'template_fill',
  REVERT = 'revert',
}

/**
 * Mirrors PHP enum App\Libraries\Facades\Neuron\Workflows\AiChatImageEditor\Responses\SessionProgress.
 */
export enum SessionProgress {
  FRESH = 'fresh',
  ITERATING = 'iterating',
  REPEATING_REQUEST = 'repeating_request',
  SATISFIED = 'satisfied',
}

/**
 * Mirrors PHP enum App\Libraries\Facades\Neuron\Workflows\AiChatImageEditor\Responses\LastUserSignal.
 */
export enum LastUserSignal {
  NEW_BRIEF = 'new_brief',
  EDIT_DIRECTION = 'edit_direction',
  CLARIFICATION_REPLY = 'clarification_reply',
  FRUSTRATION = 'frustration',
  SATISFACTION = 'satisfaction',
  REDIRECT = 'redirect',
}

export interface SelectedImageAsset {
  imagePart: SelectedImageAssetPart;
  selectionReason?: string | null;
  role?: SelectedImageAssetRole | (string & {}) | null;
  assetShape?: SelectedImageAssetShape | (string & {}) | null;
  framing?: SelectedImageAssetFraming | (string & {}) | null;
}

export interface ImageGenerationContextSnapshot {
  summary: string;
  selectedAssets: SelectedImageAsset[];
  mode?: ImageGenerationContextMode | (string & {}) | null;
  sessionGoal?: string | null;
  sessionProgress?: SessionProgress | (string & {}) | null;
  lastUserSignal?: LastUserSignal | (string & {}) | null;
  providerDowngradedFrom?: string | null;
  providerReroutedFrom?: string | null;
}

export interface AiChatAnimationMetadata {
  animationHashedId: string;
  width: number;
  height: number;
  duration: number;
  hasAudio: boolean;
  model: string;
  startTime?: number;
  endTime?: number;
}

export interface ChatMessageModelImagePart {
  type: ChatMessagePartType.MODEL_GENERATED_IMAGE;
  provider?: string;
  model?: string;
  hashedId: string;
  width: number | null;
  height: number | null;
  isNewGeneration?: boolean;
  showFirstImageGenerationDelight?: boolean;
  base64ImageData?: string;
  isRawBlurred?: boolean;
  isSkeletonPlaceholder?: boolean;
  animations?: AiChatAnimationMetadata[];
  hasLayeredPoster?: boolean;
}

export type ChatMessageUserAttachment =
  | ChatMessageUserImagePart
  | ChatMessageDesignPart
  | ChatMessageImageGraphicItemSnapshotPart
  | ChatMessageExampleImageAssetPart
  | ReattachedModelImage;

export type ImagePart = ChatMessageUserAttachment | ChatMessageModelImagePart;

export interface ChatMessageModelTextPart extends ChatMessageTextPart {
  provider: string;
}

export type ChatMessagePart = ChatMessageModelTextPart | ChatMessageTextPart | ImagePart | ChatMessageComponentPart | ChatMessageUserDocumentPart;

export const isDocumentPart = (part: ChatMessagePart): part is ChatMessageUserDocumentPart => part.type === ChatMessagePartType.USER_DOCUMENT;

export enum FailedMessageType {
  INTERNAL_ERROR = 'internal_error',
  STOPPED = 'stopped',
  TOO_MANY_REQUESTS = 'too_many_requests',
  CREDITS_EXHAUSTED = 'credits_exhausted',
  USER_IMAGE_PROCESSING_ERROR = 'user_image_processing_error',
  DAILY_LIMIT_REACHED = 'daily_limit_reached',
  CONTENT_POLICY_VIOLATION = 'content_policy_violation',
  UNSUPPORTED_LANGUAGE = 'unsupported_language',
}

interface Message {
  role: MessageRole;
  parts: ChatMessagePart[];
  messageId?: string;
  deletedAt?: string | null;
}

export interface UserMessage extends Message {
  role: MessageRole.USER;
  requestPreferences?: RequestPreferences;
}

export interface ModelMessageFeedback {
  type: FeedbackType;
  comment: string | null;
  negativeFeedbackPreset: AIChatNegativeFeedbackTypes | null;
}

export interface ModelMessage extends Message {
  feedback: ModelMessageFeedback | null;
  role: MessageRole.ASSISTANT;
  usage: Usage;
  isThinking: boolean;
  enhancedPrompt?: string | null;
  mode?: ImageGenerationContextMode | null;
  context?: ImageGenerationContextSnapshot | null;
  producedByAgent?: string | null;
}

export interface FailedMessage extends Message {
  role: MessageRole.DEVELOPER;
  failureType: FailedMessageType;
}

export interface DeveloperMessage extends Message {
  role: MessageRole.DEVELOPER;
}

export type ChatMessage = UserMessage | ModelMessage | FailedMessage | DeveloperMessage;

export interface PendingSurvey extends SurveySpec {
  messageSessionUid: string;
}

export interface MessagesResponse {
  idchat: string;
  history: ChatMessage[];
  activeMessageSessionUid?: string;
  hasWriteAccess: boolean;
  isDeleted?: boolean;
  searchTags?: string[] | null;
  source?: AiChatSource | null;
  nextSuggestionButtons?: Record<string, unknown>;
  pendingSurvey?: PendingSurvey | null;
}

export const NEXT_SUGGESTIONS_MESSAGE_ID = 'next-suggestions';

export interface BaseModeSpecificProps {
  mode: ChatMode;
}

export interface AiChatProps extends BaseModeSpecificProps {
  mode: ChatMode.CREATE;
}

export interface EditMessageProps extends BaseModeSpecificProps {
  mode: ChatMode.EDIT_MESSAGE;
  editedMessageId: string;
  previousMessageId?: string;
}

export type ModeSpecificProps = AiChatProps | EditMessageProps;

export interface AiChatWindowProps {
  id: string;
  onSendMessage?: (chatId: string, parts: ChatMessagePart[], requestConfig?: RequestConfig) => void;
  onStopChat?: (chatId: string) => void;
  currentlyOpenedChatId?: string;
  setCurrentChatId?: (chatId: string | undefined, options?: SetChatIdNavigationOptions) => void;
  onChatStart?: VoidFunction;
  onChatComplete?: (messages: Record<string, ChatMessage>) => Promise<void>;
  chatOnLoadParams?: ChatOnLoadParams;
  onChatOnLoadConsumed?: VoidFunction;
  refreshChatHistory?: VoidFunction;
  modeSpecificProps: ModeSpecificProps;
  refreshCredits: () => void;
  credits: number;
  isUserLoggedIn: boolean;
  onLoginSuccessActions: () => void;
  hasFloatingPromptBox: boolean;
  userHasChatHistory: boolean;
  onNewChat?: VoidFunction;
  mode?: AiAgentMode;
  surfaceBias?: SurfaceBias;
}

export interface StreamEvent {
  event: string;
  type: string;
  data?: string | string[] | Record<string, unknown>;
  message?: ChatMessage;
}

export enum StreamEventName {
  CONTROL = 'control',
  THINKING = 'thinking',
  CHAT = 'chat',
  IMAGE = 'image',
  FAILED_MESSAGE = 'failed_message',
  MODEL_MESSAGE_METADATA = 'model_message_metadata',
  SURVEY = 'survey',
  COMPONENT = 'component',
}

export enum ControlEventType {
  NEW_CHAT = 'new_chat',
  CHAT_TITLE = 'chat_title',
  THUMBNAIL_URL = 'thumbnail_url',
  CREDITS_REFRESH = 'credits_refresh',
  IMAGE_STREAMING_START = 'image_streaming_start',
  CLOSE = 'close',
  MESSAGE_EDIT_APPLIED = 'message_edit_applied',
  SEARCH_TAGS = 'search_tags',
  DOWNLOAD = 'download',
  SHARE = 'share',
  OPEN_IN_EDITOR = 'open_in_editor',
  POST_ON_SOCIAL = 'post_on_social',
  PROMOTE_AS_EVENT = 'promote_as_event',
  SEND_AS_EMAIL = 'send_as_email',
  SAVE_TO_MY_DESIGNS = 'save_to_my_designs',
  INSUFFICIENT_CREDITS = 'insufficient_credits',
  ENTITY_SKELETON_START = 'entity_skeleton_start',
  ASPECT_RATIO_PIN_DROPPED = 'aspect_ratio_pin_dropped',
}

export interface ControlStreamEvent extends StreamEvent {
  event: StreamEventName.CONTROL;
  type: ControlEventType;
  idAiChat: string;
  data?: {
    aspectRatio?: string;
    /** Exact pixel target the upcoming generation will be rendered at (deterministic-size providers). */
    width?: number;
    height?: number;
    assetHashedFilename?: string;
    chatTitle?: string;
    editedMessageId?: string;
    searchTags?: string[];
    /** Full updated history (non-deleted) when backend sends MESSAGE_EDIT_APPLIED */
    history?: ChatMessage[];
    imageHashedId?: string;
    /** Stream component type of the skeleton to show on ENTITY_SKELETON_START */
    componentType?: string;
    /** Placeholder widths the BUTTON_GROUP_SKELETON renders, one brick per entry */
    buttonWidths?: string[];
    mode?: string;
  };
}

export enum StreamEventDataType {
  TEXT = 'text',
  GENERATION = 'generation',
  RAW = 'raw',
}

export interface ProgressStreamEvent extends StreamEvent {
  event: StreamEventName.THINKING;
  type: StreamEventDataType.TEXT;
  data: string[];
  forceScrollToBottom: boolean;
  idAiChat: string;
}

export interface TextStreamEvent extends StreamEvent {
  event: StreamEventName.CHAT;
  type: StreamEventDataType.TEXT;
  message: ModelMessage;
  idAiChat: string;
}

export interface FailedMessageStreamEvent extends StreamEvent {
  event: StreamEventName.FAILED_MESSAGE;
  type: FailedMessageType;
  message: ChatMessage;
  idAiChat: string;
}

export interface ImageStreamEvent extends StreamEvent {
  event: StreamEventName.IMAGE;
  type: StreamEventDataType.GENERATION;
  message: ModelMessage;
  idAiChat: string;
  showFirstImageGenerationDelight: boolean;
}

export interface RawImageStreamEvent extends StreamEvent {
  event: StreamEventName.IMAGE;
  type: StreamEventDataType.RAW;
  base64ImageData: string;
  idAiChat: string;
  provider: string;
  textContent?: string | null;
}

export interface ModelMessageMetadataStreamEvent extends StreamEvent {
  event: StreamEventName.MODEL_MESSAGE_METADATA;
  idAiChat: string;
  messageId: string;
  enhancedPrompt: string | null;
  context: ImageGenerationContextSnapshot | null;
  producedByAgent: string | null;
}

export interface ComponentStreamEvent extends StreamEvent {
  event: StreamEventName.COMPONENT;
  componentType: string;
  props: Record<string, unknown>;
  idAiChat: string;
  messageId: string;
}

export interface SurveyStreamEvent extends StreamEvent {
  event: StreamEventName.SURVEY;
  idAiChat: string;
  spec: SurveySpec;
}

export interface ActiveSurvey {
  spec: SurveySpec;
}

export type StreamDataChunk =
  | ControlStreamEvent
  | ProgressStreamEvent
  | TextStreamEvent
  | FailedMessageStreamEvent
  | ImageStreamEvent
  | RawImageStreamEvent
  | SurveyStreamEvent
	| ModelMessageMetadataStreamEvent
  | ComponentStreamEvent;

export interface SetChatIdNavigationOptions {
  replaceHistory?: boolean;
}

export interface ChatOnLoadParams {
  userRequestParts?: ChatMessagePart[];
  source?: AiChatSource;
  requestInstruction?: RequestInstruction;
  sendOnLoad?: boolean;
  replaceHistoryOnChatCreation?: boolean;
  preferences?: RequestPreferences;
}

export type AiChatStreamParams = {
  chatId?: string;
  type: ChatType;
  userRequestParts: ChatMessagePart[];
  messageSessionUid?: string;
  requestConfig?: RequestConfig;
  timezone: string;
  surfaceBias?: SurfaceBias;
  isEmbedded?: 1;
} & ModeSpecificProps;

export enum ChatMode {
  CREATE = 'create',
  EDIT_MESSAGE = 'edit-message',
}

export enum ChatType {
  DESIGN = 'design',
  AGENT = 'agent',
  /**
   * Legacy: set by event promotion chats created before the type carried the
   * experience. Never sent by the client; only returned for historical chats.
   */
  ASSET = 'asset',
}

export enum ChangeStyleTheme {
  MINIMALIST = 'minimalist',
  BOLD_MODERN = 'bold_modern',
  LUXURY_ELEGANT = 'luxury_elegant',
  NEON_FUTURISTIC = 'neon_futuristic',
  VIBRANT_COLOURFUL = 'vibrant_colourful',
  WATERCOLOR_SOFT = 'watercolor_soft',
  PAPERCUT_STYLE = 'papercut_style',
  SOFTCLAY = 'softclay',
  HAND_DRAWN = 'hand_drawn',
  EDITORIAL = 'editorial',
  PROFESSIONAL = 'professional',
}

export enum RequestInstruction {
  INJECT_GALLERY_PREVIEW_DIALOG_PROMPT = 'inject_gallery_preview_dialog_prompt',
}

export enum DecisionsAssist {
  ON = 'on',
  OFF = 'off',
}

/**
 * Settings the user pinned in the prompt box's settings popup. Every field is an explicit user selection,
 * never an inferred or derived value.
 */
export interface RequestPreferences {
  aspectRatio?: string;
  brandKitId?: string;
  decisionsAssist?: DecisionsAssist;
}

export interface RequestConfig {
  enhancedPrompt?: string;
  loadingTexts?: string[];
  changeStyleTheme?: ChangeStyleTheme;
  requestInstruction?: RequestInstruction;
  preferences?: RequestPreferences;
  approvedDraftId?: string;
  approvedOptionId?: string;
  surveyPicks?: SurveyPicks;
  pointedAt?: PointedAtEntity;
}

export enum FeedbackType {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export enum AiChatSource {
  GALLERY_PREVIEW_DIALOG = 'gallery_preview_dialog',
  MY_STUFF = 'my_stuff',
  MY_STUFF_SIDEBAR = 'my_stuff_sidebar',
  MY_STUFF_INTRO_VIDEO = 'my_stuff_intro_video',
  MY_STUFF_DESIGN_OPTIONS = 'my_stuff_design_options',
  GALLERY_BANNER = 'gallery_banner',
  HOMEPAGE = 'homepage',
  NAVBAR = 'navbar',
  PRICING_PAGE = 'pricing_page',
  GALLERY_CREATE_WITH_AI = 'gallery_create_with_ai',
  NAVBAR_PROMPT_BOX = 'navbar_prompt_box',
  MY_STUFF_SEARCH = 'my_stuff_search',
  EXTERNAL_MARKETING = 'external_marketing',
  AI_MARKETING_PAGE = 'ai_marketing_page',
  ART_TEMPLATE_PAGE = 'art_template_page',
  EVENTS_CALENDAR_PROMOTION = 'events_calendar_promotion',
  AI_CHAT_PAGE = 'ai_chat_page',
  SEO_LANDING_PAGE = 'seo_landing_page',
  MY_STUFF_MARKETING_PLANNER = 'my_stuff_marketing_planner',
  PINTEREST_RSS = 'pinterest_rss',
  MARKETING_PAGE = 'marketing_page',
  HALLOWEEN_SMALL_BUSINESS_PAGE = 'halloween_small_business_page',
  FAQ_PAGE = 'faq_page',
}

export const isAiChatSource = (value: unknown): value is AiChatSource => {
  return typeof value === 'string' && (Object.values(AiChatSource) as string[]).includes(value);
};

/**
 * Selects which orchestrator context injection the marketing agent applies for the
 * turn — set by the embedding host, decoupled from AiChatSource. Mirrors the backend
 * SurfaceBias enum; DEFAULT (or omitting it) keeps the agent unbiased.
 */
export enum SurfaceBias {
  DEFAULT = 'default',
  EVENT_PROMOTION = 'event_promotion',
}

export const isSurfaceBias = (value: unknown): value is SurfaceBias => {
  return Object.values(SurfaceBias).some((member) => {
    return member === value;
  });
};

export const CHAT_NOT_FOUND_ERROR_CODE = 'chat_not_found';
